import './DCSComboBox.css'
import { ComboBox, ComboBoxChangeEvent, ComboBoxCloseEvent, ComboBoxFilterChangeEvent, ComboBoxFocusEvent, ComboBoxOpenEvent, ListItemProps } from '@progress/kendo-react-dropdowns'
import { useEffect, useRef, useState } from 'react'
import { FilterDescriptor, filterBy } from '@progress/kendo-data-query'
import ReactDOM from 'react-dom'

interface IProps {
    label?: string
    placeholder?: string
    data: Array<string>
    allowEntry?: boolean
    value?: string
    onFilterChange?: Function
    onChange?: Function
    defaultValue?: string
    id?: string,
    name?: string,
    loading?: boolean,
    disabled?: boolean,
    style?: React.CSSProperties
    maxLength?: number
    onFocus?: (event: ComboBoxFocusEvent) => void
    onOpen?: (event: ComboBoxOpenEvent) => void
    onClose?: (event: ComboBoxCloseEvent) => void
}

const DCSComboBox = ({
    placeholder,
    label,
    value,
    allowEntry,
    data,
    onFilterChange,
    onChange,
    defaultValue,
    id,
    name,
    loading,
    disabled,
    style,
    maxLength,
    onFocus: externalOnFocus,
    onOpen: externalOnOpen,
    onClose: externalOnClose,
    ...otherProps
}: IProps) => {
    const comboRef = useRef(null)
    const [valueFilterData, setValueFilterData] = useState<Array<string>>(data)
    const [opened, setOpened] = useState<boolean>(false)

    const filterData = (filter: FilterDescriptor) => {
        const currentData = data.slice()
        return filterBy(currentData, filter)
    }

    const handleValueFilterChange = (event: ComboBoxFilterChangeEvent) => {
        const value: string = event.filter.value
        let newData = filterData(event.filter)
        if (allowEntry && allowEntry === true) {
            const oldValue = value
            newData = newData.filter(t => t != oldValue)
            if (value != '')
                newData.push(value)
        }

        if (!value || value.toString().trim() === '') {
            setValueFilterData(data) //If null (user hit the x, reset the dropdown to intial value)
            if (onFilterChange)
                onFilterChange(undefined)
        }
        else {
            setValueFilterData(newData)
            if (onFilterChange)
                onFilterChange(value)
        }

    }

    const localOnChange = (event: ComboBoxChangeEvent) => {
        if (onChange) {
            onChange(event)
        }
    }

    // otherProps may still carry Kendo Form's internal onFocus (visited-tracking only, does not open the popup), so
    // externalOnFocus/onOpen/onClose are extracted above and explicitly composed with our own opening logic here.
    const handleFocus = (event: ComboBoxFocusEvent) => {
        if (!disabled)
            setOpened(true)
        externalOnFocus?.(event)
    }

    const handleOpen = (event: ComboBoxOpenEvent) => {
        setOpened(true)
        externalOnOpen?.(event)
    }

    const handleClose = (event: ComboBoxCloseEvent) => {
        setOpened(false)
        externalOnClose?.(event)
    }

    useEffect(() => {
        setValueFilterData(data)
    }, [data])

    useEffect(() => {
        // Set the maxLength of the input field
        const nativeInput = (ReactDOM.findDOMNode(comboRef.current) as HTMLElement)?.querySelector('input')
        if (nativeInput && maxLength && maxLength > 0)
            nativeInput.maxLength = maxLength
    }, [])


    return (
        <div>
            <ComboBox
                ref={comboRef}
                name={name}
                data={valueFilterData.length > 0 ? valueFilterData : data}
                id={id}
                label={label}
                value={!value ? null : value} //null works to clear the value but undefined does not for some reason
                defaultValue={!defaultValue ? null : defaultValue}
                allowCustom={allowEntry === undefined ? false : allowEntry}
                placeholder={placeholder ? placeholder : 'Select or Type Values'}
                filterable={true}
                opened={opened}
                onFocus={handleFocus}
                onOpen={handleOpen}
                onClose={handleClose}
                onFilterChange={handleValueFilterChange}
                onChange={localOnChange}
                className='dcsCombo'
                loading={loading}
                disabled={disabled}
                style={style}
                {...otherProps}
            />
        </div>
    )
}

export default DCSComboBox
